import { apiUrl } from "../http/apiConfiguration";
import { requestJson, withQuery } from "../http/httpClient";
import { readArray, readObject } from "../http/jsonDecoding";
import {
  readDataset,
  readDatasetVersion,
  readAcceptanceStep,
  readExtraction,
  readExtractionSummary,
  type AcceptanceStep,
  type CurationState,
  type Dataset,
  type DatasetVersion,
  type Extraction,
  type ExtractionSummary,
  type PackageFormat,
} from "./knowledgeDataDecoders";
import { readAcceptedResult, readDocumentResult, readTermUse, type AcceptedResult, type DocumentResult, type TermUse } from "./lensDecoders";

/**
 * Knowledge data (`docs/components/knowledge-data.md`): extraction results and their
 * curation, datasets with immutable versions and their byte-stable packages. Releasing and
 * ResearchSpace are in `releasesApi.ts`.
 */

export const project = (projectId: string): `/${string}` => `/projects/${encodeURIComponent(projectId)}`;
const extraction = (source: string, suffix = ""): `/${string}` => {
  const [threadId = "", entry = ""] = source.split("#");
  const [number = "", revision] = entry.split("/r");
  const path = `/knowledge-data/extractions/${encodeURIComponent(threadId)}/${encodeURIComponent(number)}${suffix}` as const;
  return revision === undefined ? path : `${path}?revision=${encodeURIComponent(revision)}`;
};
const dataset = (datasetId: string): `/${string}` => `/datasets/${encodeURIComponent(datasetId)}`;
export const version = (datasetId: string, number: number): `/${string}` => `${dataset(datasetId)}/versions/${String(number)}`;

export const items = <T>(read: (item: unknown) => T) => (body: unknown) => readArray(readObject(body, "list")["items"], "items", read);

export function listExtractions(
  projectId: string,
  state: CurationState | null,
  signal?: AbortSignal,
): Promise<ExtractionSummary[]> {
  return requestJson({
    method: "GET",
    path: withQuery(`${project(projectId)}/knowledge-data/extractions`, { state }),
    signal,
    decodeResponse: items(readExtractionSummary),
  });
}

/** The results the caller may start a run deciding now, newest first: what "Accept the knowledge data" offers to pick (A8). */
export function listDecidableResults(projectId: string, signal?: AbortSignal): Promise<ExtractionSummary[]> {
  return requestJson({
    method: "GET",
    path: withQuery(`${project(projectId)}/knowledge-data/extractions`, { decidable: "true" }),
    signal,
    decodeResponse: items(readExtractionSummary),
  });
}

/** What was recorded on any version of a document, both kinds, every state, newest first (the Documents lens). */
export function listDocumentResults(projectId: string, documentId: string, signal?: AbortSignal): Promise<DocumentResult[]> {
  return requestJson({
    method: "GET",
    path: `${project(projectId)}/knowledge-data/documents/${encodeURIComponent(documentId)}/extractions`,
    signal,
    decodeResponse: items(readDocumentResult),
  });
}

/** What is accepted on one exact document version (`<item id>@<blob>`): each accepted result with the statements said on it (F94). */
export function listAcceptedStatements(projectId: string, documentVersion: string, signal?: AbortSignal): Promise<AcceptedResult[]> {
  return requestJson({
    method: "GET",
    path: `${project(projectId)}/knowledge-data/document-versions/${encodeURIComponent(documentVersion)}/accepted-statements`,
    signal,
    decodeResponse: items(readAcceptedResult),
  });
}

/** The instance-data results that use a term, as a class or as a property (the term lens). */
export function listTermUses(projectId: string, iri: string, signal?: AbortSignal): Promise<TermUse[]> {
  return requestJson({
    method: "GET",
    path: withQuery(`${project(projectId)}/knowledge-data/term-uses`, { iri }),
    signal,
    decodeResponse: items(readTermUse),
  });
}

export function getExtraction(source: string, signal?: AbortSignal): Promise<Extraction> {
  return requestJson({ method: "GET", path: extraction(source), signal, decodeResponse: readExtraction });
}

/**
 * The step that decides the result (ADR-0026 slice 3a): its acceptance step, started now for
 * a curator if none was asked for it. A result is accepted or rejected only there.
 */
export function openReview(source: string): Promise<AcceptanceStep> {
  return requestJson({ method: "POST", path: extraction(source, "/review"), decodeResponse: readAcceptanceStep });
}

export function listDatasets(projectId: string, signal?: AbortSignal): Promise<Dataset[]> {
  return requestJson({ method: "GET", path: `${project(projectId)}/datasets`, signal, decodeResponse: items(readDataset) });
}

export function getDataset(datasetId: string, signal?: AbortSignal): Promise<Dataset> {
  return requestJson({ method: "GET", path: dataset(datasetId), signal, decodeResponse: readDataset });
}

export function getDatasetVersion(datasetId: string, number: number, signal?: AbortSignal): Promise<DatasetVersion> {
  return requestJson({ method: "GET", path: version(datasetId, number), signal, decodeResponse: readDatasetVersion });
}

/** The address of a version's package; the browser downloads it with the session cookie. */
export function packageUrl(datasetId: string, number: number, format: PackageFormat): string {
  return apiUrl(withQuery(`${version(datasetId, number)}/package`, { format }));
}

